interface Testimonial {
  name: string;
  company: string;
  role?: string;
  quote: string;
  rating?: number;
  initials: string;
  color: string;
}

interface SharedTestimonialsProps {
  title?: string;
  subtitle?: string;
  testimonials?: Testimonial[];
}

const DEFAULT_TESTIMONIALS: Testimonial[] = [
  {
    name: 'Marcus T.',
    company: 'Bayou Consulting Group',
    role: 'Office Manager',
    quote: 'OJS has been cleaning our Metairie office for over three years. Reliable, consistent, and the team is always professional. Our office has never looked this good — clients notice immediately when they walk in.',
    rating: 5,
    initials: 'MT',
    color: 'bg-emerald-500',
  },
  {
    name: 'Sandra R.',
    company: 'Lakeside Medical Center',
    role: 'Facilities Director',
    quote: 'We needed a certified janitorial company that could handle a medical environment. OJS delivered with GBAC-certified protocols and a team that truly understands commercial sanitation. Highly recommended for healthcare facilities.',
    rating: 5,
    initials: 'SR',
    color: 'bg-teal-600',
  },
  {
    name: 'David L.',
    company: 'Jefferson Parish Commercial Properties',
    role: 'Property Manager',
    quote: 'Managing multiple buildings means I need a cleaning partner I can trust without micromanaging. OJS handles all our properties seamlessly. Professional team, great communication, and consistent quality every single time.',
    rating: 5,
    initials: 'DL',
    color: 'bg-cyan-600',
  },
  {
    name: 'Patricia M.',
    company: 'New Orleans Tech Hub',
    role: 'Operations Lead',
    quote: 'From the initial walkthrough to daily service, OJS made everything easy. Their customized cleaning plan fit our open-office layout perfectly. The crew is discreet, thorough, and always finishes before our team arrives.',
    rating: 5,
    initials: 'PM',
    color: 'bg-green-600',
  },
];

export default function SharedTestimonials({
  title = 'What Our Clients Say',
  subtitle = 'Trusted by offices, commercial facilities, and government buildings throughout the Greater New Orleans area.',
  testimonials = DEFAULT_TESTIMONIALS,
}: SharedTestimonialsProps) {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-accent/10 border border-accent/20 rounded-full px-4 py-1.5 mb-5">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <i key={i} className="ri-star-fill text-accent text-xs"></i>
              ))}
            </div>
            <span className="text-accent text-xs font-body font-semibold uppercase tracking-wider">Client Reviews</span>
          </div>
          <h2 className="font-heading font-extrabold text-navy text-4xl xl:text-5xl mb-4">{title}</h2>
          <p className="text-gray-500 font-body text-lg max-w-2xl mx-auto">{subtitle}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {testimonials.map((t, i) => (
            <div
              key={i}
              className="bg-gray-50 rounded-2xl p-8 border border-gray-100 hover:border-accent/20 transition-all duration-300 hover:-translate-y-0.5 group"
            >
              {/* Stars */}
              <div className="flex gap-1 mb-5">
                {[...Array(t.rating ?? 5)].map((_, j) => (
                  <i key={j} className="ri-star-fill text-accent text-sm"></i>
                ))}
              </div>

              {/* Quote */}
              <p className="text-gray-600 font-body text-base leading-relaxed mb-6 italic">
                &ldquo;{t.quote}&rdquo;
              </p>

              {/* Author */}
              <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                <div className={`w-11 h-11 flex items-center justify-center ${t.color} rounded-full shrink-0`}>
                  <span className="text-white font-heading font-bold text-sm">{t.initials}</span>
                </div>
                <div>
                  <div className="font-heading font-bold text-navy text-sm">{t.name}</div>
                  <div className="text-gray-400 font-body text-xs">{t.role ? `${t.role}, ` : ''}{t.company}</div>
                </div>
                <i className="ri-double-quotes-r text-gray-200 text-3xl ml-auto"></i>
              </div>
            </div>
          ))}
        </div>

        {/* Google Badge */}
        <div className="flex items-center justify-center gap-4 mt-12 pt-10 border-t border-gray-100">
          <div className="flex gap-1">
            {[...Array(5)].map((_, i) => (
              <i key={i} className="ri-star-fill text-accent text-lg"></i>
            ))}
          </div>
          <div className="text-center">
            <div className="font-heading font-bold text-navy text-base">5.0 / 5.0 Rating</div>
            <div className="text-gray-400 font-body text-xs">Based on Google Reviews — Metairie, LA</div>
          </div>
          <img
            src="https://ojsjanitorial.net/wp-content/uploads/2023/04/logo.png"
            alt="OJS Janitorial"
            className="h-10 w-auto object-contain opacity-60"
          />
        </div>
      </div>
    </section>
  );
}
